import React from "react";
import {
  useDeletePosition,
  useGetDepartmentById,
  useGetDepartments,
  useUpdatePosition,
} from "../../query/DepartmentQuery";
import {
  Layout,
  List,
  Button,
  Space,
  Table,
  TableColumnType,
  Tag,
  Divider,
  Card,
  Empty,
  Input,
  Form,
  Select,
  Popconfirm,
} from "antd";
import { IPosition } from "../../models/Department";
import { PositionLevel } from "../../constants/common";

import ModalAddPosition from "../../components/modal/ModalAddPosition";
import { notify } from "../../utils/toast";

const PositionSystem = () => {
  const [selectedDepartmentId, setSelectedDepartmentId] =
    React.useState<number>();
  const { data: departments, isLoading: isLoadingDepartments } =
    useGetDepartments();
  const { data: departmentDetail, isLoading: isLoadingPositions } =
    useGetDepartmentById(selectedDepartmentId || 0);
  const [selectedPosition, setSelectedPosition] = React.useState<
    IPosition | undefined
  >(undefined);
  const [form] = Form.useForm();
  const [openModalAddPosition, setOpenModalAddPosition] = React.useState(false);
  const updatePosition = useUpdatePosition();
  const deletePosition = useDeletePosition();

  React.useEffect(() => {
    if (selectedPosition) {
      form.setFieldsValue({
        positionCode: selectedPosition.positionCode,
        positionTitle: selectedPosition.positionTitle,
        level: selectedPosition.level,
        description: selectedPosition.description,
      });
    }
  }, [selectedPosition, form]);

  const handleUpdatePosition = async () => {
    try {
      const values = await form.validateFields();
      await updatePosition.mutateAsync({
        id: selectedPosition?.id || 0,
        ...values,
      });
      notify.success("Cập nhật vị trí thành công");
      setSelectedPosition(undefined);
    } catch (error) {
      notify.error("Cập nhật vị trí thất bại");
    }
  };
  const handleDeletePosition = async (id: number) => {
    try {
      await deletePosition.mutateAsync(id);
      notify.success("Xóa vị trí thành công");
    } catch (error) {
      notify.error("Xóa vị trí thất bại");
    }
  };

  const positionColumns: TableColumnType<IPosition>[] = [
    {
      title: "Mã vị trí",
      dataIndex: "positionCode",
      key: "positionCode",
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Tên vị trí",
      dataIndex: "positionTitle",
      key: "positionTitle",
      width: 150,
    },
    {
      title: "Cấp bậc",
      dataIndex: "level",
      key: "level",
      width: 80,
      render: (level) => <Tag color="cyan">{level}</Tag>,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text) => text || "-",
    },
    {
      title: "Trạng thái",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Hoạt động" : "Không hoạt động"}
        </Tag>
      ),
    },
    {
      title: "Hành động",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => setSelectedPosition(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa vị trí này?"
            onConfirm={() => handleDeletePosition(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button type="primary" danger size="small">
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Layout.Content className="p-2">
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 4 }}
        >
          <Card
            title="Danh sách phòng ban"
            loading={isLoadingDepartments}
            style={{ height: "600px", overflowY: "auto" }}
          >
            {departments && departments.length > 0 ? (
              <List
                dataSource={departments}
                renderItem={(dept) => (
                  <List.Item
                    style={{
                      padding: "12px",
                      cursor: "pointer",
                      borderRadius: "6px",
                      marginBottom: "8px",
                      transition: "all 0.3s",
                      border:
                        selectedDepartmentId === dept.id
                          ? "1px solid #1890ff"
                          : "1px solid #d9d9d9",
                    }}
                    onClick={() => setSelectedDepartmentId(dept.id)}
                  >
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          marginBottom: "4px",
                        }}
                      >
                        {dept.departmentName} - {dept.departmentCode}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                        }}
                      >
                        {dept.description || "Không có mô tả"}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Không có phòng ban" />
            )}
          </Card>
          <Card
            title={
              selectedDepartmentId
                ? `Vị trí - Phòng ${departmentDetail?.departmentName || ""}`
                : "Chọn phòng ban để xem vị trí"
            }
            loading={isLoadingPositions}
            extra={
              selectedDepartmentId && (
                <Button
                  type="primary"
                  onClick={() => setOpenModalAddPosition(true)}
                >
                  Thêm vị trí
                </Button>
              )
            }
          >
            {selectedDepartmentId ? (
              departmentDetail?.positions &&
              departmentDetail.positions.length > 0 ? (
                <Table
                  columns={positionColumns}
                  dataSource={departmentDetail.positions}
                  rowKey={(record) => record.id}
                  pagination={false}
                  size="small"
                />
              ) : (
                <Empty description="Không có vị trí nào" />
              )
            ) : (
              <Empty description="Vui lòng chọn phòng ban" />
            )}
          </Card>
        </div>
        <Divider />
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}
        >
          <Card title="Chỉnh sửa phòng ban">
            {selectedDepartmentId && departmentDetail ? (
              <div className="flex flex-col gap-2">
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: 500,
                    }}
                  >
                    Tên phòng ban
                  </label>
                  <Input
                    size="small"
                    value={departmentDetail.departmentName}
                    placeholder="Nhập tên phòng ban"
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: 500,
                    }}
                  >
                    Mô tả
                  </label>
                  <Input.TextArea
                    size="small"
                    value={departmentDetail.description || ""}
                    placeholder="Nhập mô tả phòng ban"
                    rows={3}
                  />
                </div>
                <Space>
                  <Button type="primary" disabled size="small">
                    Sửa
                  </Button>
                </Space>
              </div>
            ) : (
              <Empty description="Chọn phòng ban để chỉnh sửa" />
            )}
          </Card>
          <Card title="Chỉnh sửa vị trí">
            {selectedPosition ? (
              <Form form={form} layout="vertical" size="small">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <Form.Item
                    label="Mã vị trí"
                    name="positionCode"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Vui lòng nhập mã vị trí" },
                    ]}
                  >
                    <Input placeholder="Mã vị trí" size="small" />
                  </Form.Item>
                  <Form.Item
                    label="Tên vị trí"
                    name="positionTitle"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Vui lòng nhập tên vị trí" },
                    ]}
                  >
                    <Input placeholder="Tên vị trí" size="small" />
                  </Form.Item>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    marginTop: 12,
                  }}
                >
                  <Form.Item
                    label="Cấp bậc"
                    name="level"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Vui lòng chọn cấp bậc" },
                    ]}
                  >
                    <Select placeholder="Cấp bậc" size="small">
                      {PositionLevel.map((level) => (
                        <Select.Option key={level.value} value={level.value}>
                          {level.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Mô tả"
                    name="description"
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Mô tả" size="small" />
                  </Form.Item>
                </div>
                <Space size="small" style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    size="small"
                    loading={updatePosition.isPending}
                    onClick={() => handleUpdatePosition()}
                  >
                    Cập nhật
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedPosition(undefined)}
                  >
                    Hủy
                  </Button>
                </Space>
              </Form>
            ) : (
              <Empty description="Chọn vị trí để chỉnh sửa" />
            )}
          </Card>
        </div>
      </Layout.Content>
      <ModalAddPosition
        open={openModalAddPosition}
        departmentId={selectedDepartmentId || 0}
        departmentName={
          departments?.find((dept) => dept.id === selectedDepartmentId)
            ?.departmentName || ""
        }
        onCancel={() => setOpenModalAddPosition(false)}
      />
    </Layout>
  );
};

export default PositionSystem;
